import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getVMs, getVM, deleteVM } from 'helpers/requests/vms';
import { getBillingAccount } from 'helpers/requests/billing';
import { handleUnexpectedStatusCode } from 'helpers/errors';
import { appPaths } from 'config';
import Loading from 'components/vms/list/Loading';
import VMCard from './VMCard';
import CreateVMForm from 'components/vms/create/CreateVMForm';

const VMs = () => {
  const [vmList, setVmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [account, setAccount] = useState(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();

  let handleGetVMs = async () => {
    const [vmResult, accountResult] = await Promise.all([
      getVMs(),
      getBillingAccount()
    ]);

    if (vmResult.success) {
      setVmList(vmResult.response.data);
    } else {
      handleUnexpectedStatusCode(
        vmResult.expectedStatusCode,
        vmResult.response.status
      );
    }

    if (accountResult.success) {
      setAccount(accountResult.response.data);
    }

    setLoading(false);
  };

  const pollVM = async id => {
    const result = await getVM(id);
    if (!result.success) {
      if (isMounted.current) {
        setDeletingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setVmList(prev => prev.filter(vm => vm.id !== id));
        toast.success('Cloud Desktop deleted successfully.');
      }
    } else {
      setTimeout(() => pollVM(id), 2000);
    }
  };

  const handleDeleteVM = async id => {
    if (window.confirm('Are you sure you want to delete this Cloud Desktop?')) {
      setDeletingIds(prev => new Set(prev).add(id));
      const r = await deleteVM(id);
      if (r.success) {
        pollVM(id);
      } else {
        setDeletingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        if (r.response.status === 404) {
          toast.error('Cloud Desktop not found. Maybe it was already deleted?');
        } else {
          handleUnexpectedStatusCode(r.expectedStatusCode, r.response.status);
        }
      }
    }
  };

  useEffect(() => {
    handleGetVMs();
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (loading) return <Loading />;

  const isGracePeriod = account && account.is_in_grace_period;

  if (vmList.length === 0) {
    return <CreateVMForm />;
  }

  return (
    <>
      {/* Warning banner for grace period */}
      {isGracePeriod && (
        <Card className="mb-3 border-danger">
          <Card.Body className="d-flex align-items-center justify-content-between">
            <span className="text-danger">
              Your VMs are suspended. Subscribe to restore access or they will
              be deleted on{' '}
              {new Date(account.grace_period_ends_at).toLocaleString()}.
            </span>
            <Button
              variant="danger"
              size="sm"
              className="ms-3 text-nowrap"
              onClick={() => navigate(appPaths.settings.billing)}
            >
              Subscribe now
            </Button>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3">
        {vmList.map(vm => (
          <Col xl={3} lg={4} md={6} sm={12} key={vm.id}>
            <VMCard
              vm={vm}
              onDelete={handleDeleteVM}
              deleting={deletingIds.has(vm.id)}
            />
          </Col>
        ))}
      </Row>
    </>
  );
};

export default VMs;
